import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { DataChannelMessage } from '../../hooks/useDataChannel';
import { PiPMessage } from '../../hooks/useSessionPiP';
import { PiPControls } from './PiPControls';

interface PiPSessionWindowProps {
  pipWindow: Window;
  videoElement: HTMLVideoElement | null;
  audioRef?: React.RefObject<HTMLAudioElement | null>;
  initialVolume?: number;
  initialMuted?: boolean;

  muteMicrophone?: (muted: boolean) => void;

  // Session controls
  isAuthenticated?: boolean;
  startScreenShare?: () => Promise<void>;
  stopScreenShare?: () => void;

  // Upload controls
  socketId?: string;
  avatarId?: string;
  organizationId?: string;
  onTrackUploadEvent?: (event: string) => void;
  // Upload state sync
  initialIsFileUploading?: boolean;
  initialIsProcessing?: boolean;
  initialIsScreenSharing?: boolean;

  // Stop talking controls
  onStopTalking?: () => void;
  sessionId?: string;
  sendMessage?: (message: DataChannelMessage) => void;

  // Callbacks
  onVolumeChange?: (volume: number) => void;
  onMuteChange?: (muted: boolean) => void;
  onEndSessionComplete?: () => void;
  isMuted?: boolean;
  hideScreenShareButton: boolean;
  isWatchSession: boolean;
}

const PiPSessionWindow: React.FC<PiPSessionWindowProps> = ({
  isWatchSession,
  pipWindow,
  videoElement,
  audioRef,
  initialVolume = 1,
  initialMuted = false,
  muteMicrophone = () => {},
  isAuthenticated = false,
  initialIsScreenSharing = false,
  startScreenShare = async () => {},
  stopScreenShare = () => {},
  socketId = '',
  avatarId = '',
  organizationId,
  onTrackUploadEvent,
  initialIsFileUploading = false,
  onStopTalking,
  sessionId,
  sendMessage,
  onVolumeChange,
  onMuteChange,
  onEndSessionComplete,
  isMuted,
  hideScreenShareButton,
}) => {
  const [volume, setVolume] = useState(initialVolume);
  const [isAudioMuted, setIsAudioMuted] = useState(initialMuted);
  const [currentIsAvatarTalking, setCurrentIsAvatarTalking] = useState(false); // Start with false, will be updated via postMessage
  const pipVideoRef = useRef<HTMLVideoElement>(null);
  const [isFileUploading, setIsFileUploading] = useState(
    initialIsFileUploading,
  );
  const [isScreenSharing, setIsStreamSharing] = useState(
    initialIsScreenSharing,
  );

  // Sync video stream from main window to PiP window
  useEffect(() => {
    if (!videoElement || !pipVideoRef.current) {
      return;
    }

    const pipVideo = pipVideoRef.current;

    // Copy video properties
    pipVideo.srcObject = videoElement.srcObject;
    pipVideo.currentTime = videoElement.currentTime;
    pipVideo.muted = videoElement.muted;
    pipVideo.volume = videoElement.volume;

    // Wait for video to be ready and then sync play state
    const syncPlayback = () => {
      if (!videoElement.paused) {
        pipVideo.play().catch(console.error);
      }
    };

    if (pipVideo.readyState >= 2) {
      // Video has enough data to start playing
      syncPlayback();
    } else {
      // Wait for video to be ready
      pipVideo.addEventListener('loadeddata', syncPlayback, { once: true });
    }

    // Listen for changes in main video
    const syncVideo = () => {
      if (pipVideo.srcObject !== videoElement.srcObject) {
        pipVideo.srcObject = videoElement.srcObject;
      }
    };

    const syncPlayState = () => {
      if (videoElement.paused && !pipVideo.paused) {
        pipVideo.pause();
      } else if (!videoElement.paused && pipVideo.paused) {
        pipVideo.play().catch(console.error);
      }
    };

    // Add event listeners to main video
    videoElement.addEventListener('loadedmetadata', syncVideo);
    videoElement.addEventListener('play', syncPlayState);
    videoElement.addEventListener('pause', syncPlayState);

    return () => {
      videoElement.removeEventListener('loadedmetadata', syncVideo);
      videoElement.removeEventListener('play', syncPlayState);
      videoElement.removeEventListener('pause', syncPlayState);
    };
  }, [videoElement]);

  // Sync audio state from main window to PiP window
  useEffect(() => {
    if (!audioRef?.current) return;

    const audioElement = audioRef.current;

    // Initial sync
    setVolume(audioElement.volume);
    setIsAudioMuted(audioElement.muted);

    // Listen for volume changes from main window
    const handleVolumeChange = () => {
      setVolume(audioElement.volume);
      setIsAudioMuted(audioElement.muted);
    };

    // Add event listeners
    audioElement.addEventListener('volumechange', handleVolumeChange);

    return () => {
      audioElement.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [audioRef]);

  // Listen for real-time updates from main window
  useEffect(() => {
    const handleMessage = (event: MessageEvent<PiPMessage>) => {
      if (event.origin !== window.location.origin) return;

      const { type, data } = event.data;

      switch (type) {
        case 'avatarTalkingStateChanged': {
          if (data.isAvatarTalking !== undefined) {
            setCurrentIsAvatarTalking(data.isAvatarTalking);
          }
          break;
        }
        case 'fileUploadStateChanged': {
          if (data.isFileUploading !== undefined) {
            setIsFileUploading(data.isFileUploading);
          }
          break;
        }
        case 'shareStateChanged': {
          if (data.shareScreenState !== undefined) {
            setIsStreamSharing(data.shareScreenState);
          }
          break;
        }
      }
    };

    // Listen for messages from main window
    if (pipWindow) {
      pipWindow.addEventListener('message', handleMessage);
      return () => pipWindow.removeEventListener('message', handleMessage);
    }
  }, []);

  // Handle audio volume mute/unmute
  const handleAudioMute = useCallback(() => {
    if (audioRef?.current) {
      const newMutedState = !isAudioMuted;

      audioRef.current.muted = newMutedState;
      setIsAudioMuted(newMutedState);

      // Notify main window about mute change
      onMuteChange?.(newMutedState);
    }
  }, [isAudioMuted, audioRef, onMuteChange]);

  // Handle volume change
  const handleVolumeChange = useCallback(
    (newVolume: number) => {
      setVolume(newVolume);

      if (audioRef?.current) {
        audioRef.current.volume = newVolume;

        // If volume is set to > 0, unmute automatically
        if (newVolume > 0 && audioRef.current.muted) {
          audioRef.current.muted = false;
          setIsAudioMuted(false);
          onMuteChange?.(false);
        }

        // If volume is set to 0, mute automatically
        if (newVolume === 0 && !audioRef.current.muted) {
          audioRef.current.muted = true;
          setIsAudioMuted(true);
          onMuteChange?.(true);
        }
      }
      onVolumeChange?.(newVolume);
    },
    [audioRef, onVolumeChange, onMuteChange],
  );

  // Handle ending the entire session
  const handleEndSession = useCallback(() => {
    pipWindow.close();
    onEndSessionComplete?.();
  }, [pipWindow, onEndSessionComplete]);

  const handleScreenShareClick = useCallback(async () => {
    try {
      if (isScreenSharing) {
        stopScreenShare();
      } else {
        await startScreenShare();
      }
    } catch (error) {
      console.error('PiP: Screen sharing failed:', error);
    }
  }, [isScreenSharing, startScreenShare, stopScreenShare]);

  const content = (
    <div className="m-0 p-0 box-border font-sans bg-gray-900 text-white flex flex-col h-screen overflow-hidden">
      {/* Video Container */}
      <div className="flex-1 relative bg-black">
        <video
          ref={pipVideoRef}
          id="pipVideo"
          autoPlay
          muted
          className="w-full h-full object-cover"
        />

        {/* PiP Controls Overlay */}
        <PiPControls
          volume={volume}
          isMuted={isAudioMuted}
          onVolumeChange={handleVolumeChange}
          onMuteToggle={handleAudioMute}
          isMicMuted={isMuted}
          onMicMute={muteMicrophone}
          isAuthenticated={isAuthenticated}
          isScreenSharing={isScreenSharing}
          onScreenShareClick={handleScreenShareClick}
          isAvatarTalking={currentIsAvatarTalking}
          onEndSession={handleEndSession}
          socketId={socketId}
          avatarId={avatarId}
          organizationId={organizationId}
          onTrackUploadEvent={onTrackUploadEvent}
          // Upload state sync
          isFileUploading={isFileUploading}
          onStopTalking={onStopTalking}
          sendMessage={sendMessage}
          sessionId={sessionId}
          showScreenShareButton={!hideScreenShareButton}
          isWatchSession={isWatchSession}
        />
      </div>
    </div>
  );

  return createPortal(content, pipWindow.document.body);
};

export default PiPSessionWindow;
